import { Link } from "react-router-dom";
import Seo from "../components/Seo";

const storageRecords = [
  {
    name: "medein-cookie-choice",
    provider: "Medein",
    technology: "First-party localStorage",
    category: "Strictly necessary preference",
    status: "Interaction-triggered — set only after selecting “Acknowledge”.",
    purpose: "Remembers that the visitor has seen the Medein storage notice. It does not record consent to optional tracking.",
    duration: "Until the visitor clears this site’s browser storage",
    activation: "Set only after selecting “Acknowledge”.",
  },
  {
    name: "__cf_bm",
    provider: "Cloudflare",
    technology: "Security cookie",
    category: "Strictly necessary security",
    status: "Observed on the current Sites-hosted deployment; availability may vary with Cloudflare security configuration.",
    purpose: "Supports bot detection and protects the website from malicious traffic. Cloudflare states that it is generated independently, is not an application user ID, and does not track a visitor from site to site or session to session.",
    duration: "30 minutes after continuous inactivity",
    activation: "May be set when a visitor requests a site protected by Cloudflare bot-management features.",
  },
];

const thirdPartyServices = [
  {
    name: "Sites hosting infrastructure",
    provider: "OpenAI",
    category: "Hosting and content delivery",
    status: "Used to publish and serve the Medein website. Medein has not configured this service for advertising or visitor profiling.",
    detail: "The hosting service may process ordinary network-request and diagnostic information needed to deliver, secure, and maintain the website, such as IP address, requested URL, timestamp, browser headers, response status, and security events.",
    privacyHref: "https://openai.com/policies/privacy-policy/",
  },
  {
    name: "Cloudflare edge delivery and security",
    provider: "Cloudflare",
    category: "Content delivery, abuse prevention, and security",
    status: "Used by the current Sites-hosted deployment; the __cf_bm security cookie was observed during the 27 August 2026 review.",
    detail: "Cloudflare receives network-request information to route traffic and protect the site. Its security products may set strictly necessary cookies when the relevant protection is active. Medein does not use Cloudflare in this deployment for advertising profiling.",
    privacyHref: "https://www.cloudflare.com/privacypolicy/",
    cookieHref: "https://developers.cloudflare.com/fundamentals/reference/policies-compliances/cloudflare-cookies/",
  },
];

function StorageRecord({ record }) {
  return (
    <article className="cookie-record">
      <h3><code>{record.name}</code></h3>
      <dl>
        <div><dt>Provider</dt><dd>{record.provider}</dd></div>
        <div><dt>Technology</dt><dd>{record.technology}</dd></div>
        <div><dt>Category</dt><dd>{record.category}</dd></div>
        <div><dt>Observed status</dt><dd>{record.status}</dd></div>
        <div><dt>Purpose</dt><dd>{record.purpose}</dd></div>
        <div><dt>Duration</dt><dd>{record.duration}</dd></div>
        <div><dt>When activated</dt><dd>{record.activation}</dd></div>
      </dl>
    </article>
  );
}

export default function CookiePolicyPage() {
  return (
    <section className="legal-page cookie-policy-page">
      <Seo
        title="Cookie Policy"
        description="How the Medein website uses necessary cookies, local storage, and hosting-security technologies."
      />
      <p className="section-label">Legal</p>
      <h1>Cookie Policy</h1>
      <p>Effective and last updated: 27 August 2026</p>
      <p className="legal-intro">
        This Policy explains how cookies and similar technologies are used on the Medein website at medein.com.tw
        and www.medein.com.tw. It should be read together with our <Link to="/privacy-policy">Privacy Policy</Link>.
      </p>

      <section>
        <h2>1. Scope and responsibility</h2>
        <p>
          “Medein”, “we”, “us”, and “our” refer to the organisation operating this brand website and deciding why
          the website and its public content are provided. Where the GDPR applies, Medein is the controller for the
          purposes and means it determines. Hosting and security providers may act as processors and may be
          independently responsible for processing they determine, as described in their own notices. Questions or
          rights requests may be submitted through our <Link to="/contact">Contact page</Link>.
        </p>
      </section>

      <section>
        <h2>2. What cookies and similar technologies are</h2>
        <p>
          Cookies are small text files stored on a browser or device. Similar technologies include local storage,
          session storage, pixels, tags, and scripts that store information on, or access information from, a
          device. Session technologies expire when the browser session ends. Persistent technologies remain for a
          stated period or until the visitor deletes them.
        </p>
      </section>

      <section>
        <h2>3. Categories and EU/EEA legal basis</h2>
        <div className="cookie-category-grid">
          <article>
            <h3>Strictly necessary</h3>
            <p>Enabled only to deliver a requested page, protect the website, or remember that the storage notice has been acknowledged.</p>
          </article>
          <article>
            <h3>Functional preferences</h3>
            <p>No optional functional-preference technology is currently enabled.</p>
          </article>
          <article>
            <h3>Analytics and performance — not currently enabled</h3>
            <p>Medein has not configured analytics cookies, audience measurement tags, or conversion tracking on this independent website.</p>
          </article>
          <article>
            <h3>Advertising and social — not currently enabled</h3>
            <p>Medein has not configured advertising cookies, behavioural profiling, or social-media tracking technology on this independent website.</p>
          </article>
        </div>
        <p>
          Article 5(3) of the EU ePrivacy Directive generally requires prior consent before information is stored on,
          or accessed from, a visitor’s device. The narrow exception covers technology strictly necessary to transmit
          a communication or provide a service explicitly requested by the visitor. A GDPR legitimate-interest or
          contract basis does not by itself authorise non-essential device storage or access.
        </p>
        <p>
          No optional storage requiring consent is currently enabled. Where Medein controls personal-data processing
          needed to deliver and secure this public website, it relies on its legitimate interests under GDPR Article
          6(1)(f), subject to necessity, balancing, transparency, and visitor rights. If consent-based technology is
          introduced later, consent must be freely given, specific, informed, unambiguous, recorded, and as easy to
          withdraw as to give under GDPR Articles 6(1)(a) and 7.
        </p>
      </section>

      <section aria-labelledby="storage-inventory-heading">
        <h2 id="storage-inventory-heading">4. Current device-storage inventory</h2>
        <p>
          This inventory reflects the independent Sites-hosted deployment reviewed on 27 August 2026. Security
          configuration, browser state, or a hosting update can affect whether a strictly necessary security cookie
          appears. Medein will update this inventory before introducing a new purpose, provider, or optional technology.
        </p>
        <div className="cookie-inventory">
          {storageRecords.map((record) => <StorageRecord key={record.name} record={record} />)}
        </div>
      </section>

      <section>
        <h2>5. Hosting and third-party services</h2>
        <div className="third-party-list">
          {thirdPartyServices.map((service) => (
            <article key={service.name}>
              <p className="cookie-service-category">{service.category}</p>
              <h3>{service.name}</h3>
              <p><strong>Provider:</strong> {service.provider}</p>
              <p><strong>Current status:</strong> {service.status}</p>
              <p>{service.detail}</p>
              <p className="policy-links">
                <a
                  href={service.privacyHref}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${service.name}: provider privacy information (opens in a new tab)`}
                >Provider privacy information <span aria-hidden="true">↗</span></a>
                {service.cookieHref ? (
                  <a
                    href={service.cookieHref}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${service.name}: provider cookie information (opens in a new tab)`}
                  >Provider cookie information <span aria-hidden="true">↗</span></a>
                ) : null}
              </p>
            </article>
          ))}
        </div>
        <p>
          No separate third-party analytics, advertising, or social-media SDK is configured in the Medein application.
          Normal links to external websites do not send a visitor to those websites until the visitor follows the link.
        </p>
      </section>

      <section>
        <h2>6. Data, purposes, and retention</h2>
        <p>
          Device-storage purposes and durations are listed in the inventory above. Website delivery and security may
          involve IP address, approximate location inferred from IP, request time, requested URL, referrer when supplied,
          browser and device headers, response status, and security signals. These data are used to deliver content,
          diagnose availability, prevent abuse, and protect the website. Hosting and security logs are retained only
          for the periods established by the applicable provider configuration and agreement and as necessary for those
          purposes; available details may be requested through the Contact page.
        </p>
        <p>
          Medein does not intentionally use website storage to collect patient information, medical records, health
          information, genetic data, or other special-category data. Do not submit such information through this public website.
        </p>
      </section>

      <section>
        <h2>7. Your choices and withdrawal</h2>
        <div className="policy-notice" role="note">
          <p>
            The “Acknowledge” control dismisses the storage notice and records that dismissal locally. It is not an
            acceptance of optional tracking, and the site does not start optional analytics or advertising technology.
          </p>
        </div>
        <ul>
          <li>Clear this site’s browser storage to remove the Medein notice acknowledgement.</li>
          <li>Use browser settings to inspect, block, or delete cookies and local storage.</li>
          <li>Contact Medein to ask about the current technologies or exercise an applicable data-protection right.</li>
        </ul>
        <p>
          There is currently no optional cookie consent to withdraw. If Medein introduces a consent-based technology,
          it will be blocked until a valid choice is made, rejection will be as easy as acceptance, and an equally easy
          way to withdraw consent will remain available without affecting processing that occurred before withdrawal.
          Blocking strictly necessary security technology may prevent the protected website from working correctly.
        </p>
      </section>

      <section>
        <h2>8. International transfers</h2>
        <p>
          Hosting and security providers may process technical data outside the visitor’s country, including outside
          the EEA. Where the GDPR restricts a transfer, the responsible controller must use an applicable safeguard,
          such as an adequacy decision or the European Commission’s Standard Contractual Clauses, and supplementary
          measures where required. Provider notices describe their respective transfer practices. Contact Medein to
          request available information about safeguards relevant to Medein’s processing.
        </p>
      </section>

      <section>
        <h2>9. Your data-protection rights</h2>
        <p>
          Where the GDPR applies, you may have rights of access, rectification, erasure, restriction, portability, and
          objection, plus the right to withdraw consent when consent is the legal basis. You may lodge a complaint with
          the supervisory authority in the EEA country where you live, work, or believe an infringement occurred.
          Requests relating to Medein may be submitted through our <Link to="/contact">Contact page</Link>. You may also
          contact a provider directly about processing for which that provider is independently responsible.
        </p>
      </section>

      <section>
        <h2>10. Changes and contact</h2>
        <p>
          We will review this inventory when the hosting configuration, providers, purposes, or retention periods
          change. Material changes will be identified by a revised date. The notice acknowledgement is not used to
          authorise a changed purpose, provider, or technology, and a fresh valid choice will be requested where law
          requires it.
        </p>
        <p>
          Questions or requests concerning this Policy can be submitted through the <Link to="/contact">Medein Contact
          page</Link>. Do not include confidential research, patient, health, genetic, or other sensitive information in
          a general website inquiry.
        </p>
      </section>
    </section>
  );
}
